import React, { useCallback, useEffect } from "react";

import { compact, some } from "lodash/fp";
import { useNavigate, useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import TablePaginator from "@app/analytics/components/TablePaginator";
import useProductivityIntegrationStatus from "@app/analytics/hooks/integrations/useProductivityIntegrationStatus";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useTopicReports from "@app/analytics/hooks/topics/useTopicReports";
import useCurrentLocationRef from "@app/analytics/hooks/useCurrentLocationRef";
import { generateProcessingTopicReport } from "@app/analytics/MeetingReports/ReportsTable/utils";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import { getDateRanges } from "@app/analytics/utils";
import useAuth from "@app/common/auth/useAuth";
import { SSO_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import useMeetings from "@app/common/hooks/meetings/useMeetings";
import useLocalStorageTimeout from "@app/common/hooks/useLocalStorageTimeout";
import usePagination from "@app/common/hooks/usePagination";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { SSOProviderId } from "@app/common/types/providerTypes";
import * as SessionUtils from "@app/common/utils/sessionUtils";
import Config from "@app/config";
import { usePageTitle } from "@app/utils/hooks";

import Stack from "@rui/Stack";

import useRecurringMeetings from "./hooks/useRecurringMeetings";
import MeetingTipsBanner from "./MeetingTipsBanner";
import ReportsMenuContainer from "./ReportsMenuContainer";
import ReportsTable from "./ReportsTable";
import ReportStatusesAndUpsells from "./ReportStatusesAndUpsells";
import { OnOpenReportHandler, Report } from "./types";
import { filterReports, groupRecurringMeetings } from "./utils";
import { isFreePlan } from "../account/Billing/utils";
import useBillingContext from "../hooks/billing/useBillingContext";
import { PAGE_QUERY_PARAM_NAME } from "../MeetingReport/constants";

const Reports = () => {
  usePageTitle("Reports");

  const [queryParams, setQueryParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { teamId, userId } = useRollupContext();
  const { currentPlan } = useBillingContext();
  const { processingProvider } = useProductivityIntegrationStatus();

  const { groupRecurring, reportViews, selectedTags, sourceFilters } =
    useReportsTable((state) => ({
      groupRecurring: state.groupRecurring,
      reportViews: state.reportViews,
      selectedTags: state.tags.selectedTags,
      sourceFilters: state.sources.selected,
    }));

  const dateRanges = useDateRangeStore(getDateRanges);

  const [canShowProcessingToast, resetCanShowProcessingToast] =
    useLocalStorageTimeout("lastProcessingAlertShown");

  const hasSsoSuccess =
    queryParams.get("success") === "true" &&
    queryParams.get("action") === "add_sso";

  useEffect(() => {
    if (!hasSsoSuccess) return;

    const providerId = queryParams.get("provider");
    let message = "Your account was successfully connected";
    const provider = SSO_PROVIDERS_BY_ID[providerId as SSOProviderId];
    if (provider) {
      message = `Your ${provider.displayName} account was successfully connected`;
    }

    toast.success(message);
  }, [hasSsoSuccess]);

  const { meetings: allMeetings, isLoading: allMeetingsLoading } = useMeetings(
    {
      dateRanges,
      workspaceId: user?.workspaceId,
      userId,
      teamId,
      includeSampleMeetings: true,
    },
    { refetchOnMount: true }
  );

  const stickyTopicReport = processingProvider
    ? generateProcessingTopicReport(user, processingProvider)
    : null;

  const { topics: allTopics, isLoading: isTopicReportsLoading } =
    useTopicReports({
      dateRange: dateRanges[0],
      view: "default",
    });

  const { meetings: recurringMeetings, isLoading: recurringMeetingsLoading } =
    useRecurringMeetings(
      { dateRanges, userId, teamId },
      { enabled: groupRecurring }
    );

  const loading =
    allMeetingsLoading ||
    recurringMeetingsLoading ||
    (Config.flags.waitForTopicReportsLoad && isTopicReportsLoading);

  const reports = filterReports({
    meetings: groupRecurring
      ? groupRecurringMeetings(allMeetings, recurringMeetings)
      : allMeetings,
    topics: allTopics,
    tags: selectedTags,
    reportViews,
    sources: sourceFilters,
  });

  const page = Number(queryParams.get(PAGE_QUERY_PARAM_NAME));
  const allReports = stickyTopicReport
    ? [stickyTopicReport, ...reports]
    : reports;

  const pagination = usePagination<Report>(allReports, {
    loading,
    resetScrollOnPageChange: true,
    queryParamOptions: {
      setQueryParams,
      queryParams,
      queryParamName: PAGE_QUERY_PARAM_NAME,
    },
  });

  useEffect(() => {
    if (!loading && page <= pagination.numPages) {
      pagination.goToPage(page);
    }
  }, [page, pagination.numPages, loading]);

  const sessionsProcessingAfterUpgrade = some(
    (meeting) =>
      SessionUtils.isProcessing(meeting) && meeting.isBillingUpgraded,
    allMeetings
  );

  useEffect(() => {
    if (
      canShowProcessingToast &&
      sessionsProcessingAfterUpgrade &&
      !isFreePlan(currentPlan.type)
    ) {
      toast.success({
        type: "system",
        title: "Processing reports",
        message: `Your account has been successfully upgraded to ${currentPlan.label} (${currentPlan.billingCadence}). Past reports will be generated shortly. This process can take up to 24 hours.`,
        dismissible: true,
        sticky: true,
        action: (
          <Typography
            variant="bodySecondaryMed"
            sx={{
              color: "#007E33",
              paddingBottom: 1,
              "&:hover": { cursor: "pointer" },
            }}
            onClick={() => resetCanShowProcessingToast()}
          >
            Dismiss
          </Typography>
        ),
      });
    }
  }, [sessionsProcessingAfterUpgrade, currentPlan.type]);

  const currentLocationRef = useCurrentLocationRef();

  const handleOpenReport: OnOpenReportHandler = useCallback((id, opts) => {
    const route =
      opts.type === "topic"
        ? ROUTES.topicReportById(id, opts.ownerUserId)
        : ROUTES.meetingReportById(id);
    if (opts.newTab) {
      window.open(route, "_blank");
    } else {
      navigate(route, {
        state: {
          from: currentLocationRef.current,
        },
      });
    }
  }, []);

  return (
    <>
      <ReportStatusesAndUpsells />
      <ReportsMenuContainer isLoading={loading} pagination={pagination} />
      <MeetingTipsBanner />
      <Stack sx={{ width: "100%", overflowX: { xxs: "hidden", sm: "auto" } }}>
        <ReportsTable
          isLoading={loading}
          reports={reports}
          onOpenReport={handleOpenReport}
          pagination={pagination}
          stickyReports={compact([stickyTopicReport])}
        />
      </Stack>
      <Stack variant="right" sx={{ width: "100%", mt: 2 }}>
        <TablePaginator
          {...pagination}
          loading={loading}
          showPageSizeSelector
        />
      </Stack>
    </>
  );
};

export default Reports;
